import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, RefreshCw } from "lucide-react";
import { getResearchPulseCluster } from "../../../Services/adminResearchPulse";
import { Button } from "../../../ui/button";
import { Skeleton } from "../../../ui/skeleton";
import { TriageDisclaimer } from "./AdminResearchPulse";
import { ClusterStatusBadge } from "./ClusterTable";
import {
  HistorySection,
  NearestSection,
  PhrasesSection,
  SafetyFlagsSection,
  SampleSignalsSection,
  SignatureSection,
  TraceabilitySection,
  dialogueHref,
} from "./ClusterDetailSections";
import TriageForm from "./TriageForm";
import { clusterTitle, errorMessage, isRetrievalBug } from "./researchPulseFormat";

const BACK = "/admin/research-pulse";

const AdminResearchPulseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cluster, setCluster] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(
    async ({ quiet = false } = {}) => {
      if (!quiet) setLoading(true);
      setError(null);
      try {
        setCluster(await getResearchPulseCluster(id));
      } catch (err) {
        setError(errorMessage(err, "Failed to load the cluster"));
        if (!quiet) setCluster(null);
      } finally {
        setLoading(false);
      }
    },
    [id]
  );

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-6 w-72" />
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error && !cluster) {
    return (
      <div className="space-y-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(BACK)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      </div>
    );
  }

  if (!cluster) return null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(BACK)}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold tracking-tight">{clusterTitle(cluster)}</h1>
              <ClusterStatusBadge status={cluster.status} />
            </div>
            <p className="font-mono text-xs text-muted-foreground">{cluster.cluster_id}</p>
            {cluster.linked_dialogue_id && (
              <p className="text-sm">
                Linked Dialogue{" "}
                <Link
                  to={dialogueHref(cluster.linked_dialogue_id)}
                  className="font-mono text-primary hover:underline"
                >
                  {cluster.linked_dialogue_id}
                </Link>
              </p>
            )}
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => load({ quiet: true })}>
          <RefreshCw className="mr-2 h-4 w-4" /> Refresh
        </Button>
      </div>

      <TriageDisclaimer />

      {isRetrievalBug(cluster) && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <strong>Retrieval bug — route to QA.</strong> A suitable Dialogue exists but
            wasn't found. This is a product / engineering fix, not a research gap.
          </span>
        </div>
      )}

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      <SafetyFlagsSection flags={cluster.safety_flags} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <SignatureSection cluster={cluster} />
          <PhrasesSection phrases={cluster.phrases} />
          <NearestSection cluster={cluster} />
          <SampleSignalsSection signals={cluster.sample_signals} />
        </div>
        <div className="space-y-4">
          <TriageForm
            key={`${cluster.status}|${cluster.linked_dialogue_id || ""}`}
            cluster={cluster}
            onSaved={() => load({ quiet: true })}
          />
          <TraceabilitySection traceability={cluster.traceability} />
          <HistorySection history={cluster.history} />
        </div>
      </div>
    </div>
  );
};

export default AdminResearchPulseDetail;
